/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import Radio from 'backbone.radio';

import PhoneFormatBehavior from '@/behaviors/PhoneFormatBehavior';
import lazyLoadView from '@/util/lazyLoadView';
import showModal from '@/util/showModal';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';
import { validateForm } from '@/util/ParsleyUtils';

import loadPOCGridView from '../../../external-dashboard/loaders/POCGridViewLoader';
import loadPOCDetailsModalView from '../../../external-dashboard/loaders/POCDetailsModalViewLoader';
import UpdateCommand from '../../services/UpdateCommand';

import tmplCommandDetailsView from './CommandDetailsView.hbs';

const CommandDetailsView = View.extend({
  template: tmplCommandDetailsView,

  className: 'command-details-view',

  ui: {
    saveButton: '#saveButton',
    editButton: '#editButton',
    formInputs: '.form-inputs',
    pointsOfContactGridContainer: '#pointsOfContactGridContainer',
    addPOC: '.add-poc'
  },

  regions: {
    pointsOfContactGridContainer: '@ui.pointsOfContactGridContainer'
  },

  events: {
    'click @ui.editButton': 'onClickEditCommand',
    'click @ui.saveButton': 'onClickSaveCommand',
    'click @ui.addPOC': 'addPOC'
  },

  behaviors: {
    telephone: PhoneFormatBehavior
  },

  onRender() {
    Syphon.deserialize(this, this.model.get('commandDetails'));
    this.ui.formInputs.find(':input').prop('disabled', true);

    this.showChildView(
      'pointsOfContactGridContainer',
      lazyLoadView(() => loadPOCGridView(this.model.get('commandId'), 'int-update'))
    );
    this.ui.addPOC.hide();
  },

  onClickEditCommand(e) {
    e.preventDefault();
    this.ui.editButton.hide();
    this.ui.addPOC.show();
    this.ui.formInputs.find(':input').prop('disabled', false);
    this.getChildView('pointsOfContactGridContainer').getChildView('viewContainer').toggleDeletion(true);
    Radio.channel('sideNavSectionView').trigger('toggle:saveBtn', true);
  },

  async onSave() {
    const formData = Syphon.serialize(this.ui.formInputs);
    const addedPocs = this.getChildView('pointsOfContactGridContainer').getChildView('viewContainer').getFormData();
    formData.pocs = addedPocs;
    formData.id = this.model.get('commandId');

    try {
      await validateForm(this.ui.formInputs);
      this.ui.formInputs.find(':input').prop('disabled', true);
      this.ui.addPOC.hide();
      this.ui.editButton.show();
      this.getChildView('pointsOfContactGridContainer').getChildView('viewContainer').toggleDeletion(false);
      Radio.channel('sideNavSectionView').trigger('toggle:saveBtn', false);
      await UpdateCommand(formData);
      this.showSaveSuccess();
    } catch (err) {
      if (err) {
        this.showSaveError();
      }
    }
  },

  async addPOC(e) {
    e.preventDefault();
    try {
      const formData = await showModal({
        title: 'Add Point of Contact',
        dialogClasses: 'modal-lg',
        view: lazyLoadView(() => loadPOCDetailsModalView(this.model.get('commandId')))
      });
      if (formData) {
        formData.name = `${formData.firstName} ${formData.lastName}`;
        this.getChildView('pointsOfContactGridContainer')
          .getChildView('viewContainer')
          .options.collection.unshift(formData);
      }
    } catch (err) {
      this.showSavePOCError();
      throw new Error(err);
    }
  },

  showSaveSuccess() {
    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Command Saved',
      message: 'This command has been saved successfully.',
      hideAfter: 5
    });
  },

  showSaveError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage:
        'An error occurred while saving the command. Try again or contact an administrator if your issue persists.'
    });
  },

  showSavePOCError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage:
        'An error occurred while saving point of contact. Try again or contact an administrator if your issue persists.'
    });
  }
});

export default CommandDetailsView;
